import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSupabase } from '../contexts/SupabaseContext';
import { useBusiness } from '../contexts/BusinessContext';
import {
  buildPayrollRequiredActions,
  type PayrollRequiredActionRow,
} from '../lib/payroll/requiredActions';
import { createBusinessCache } from '../lib/controlCentre/actionsCache';

const payrollRequestsCache = createBusinessCache<PayrollRequiredActionRow[]>({
  persistKey: 'payroll-requests',
});

/** Pending payroll requests (time changes, add entry, add flat rate) for the control-centre inbox. */
export function usePayrollRequiredActions(enabled = true) {
  const supabase = useSupabase();
  const { selectedBusiness } = useBusiness();
  const businessId = selectedBusiness?.id ?? '';

  const [requests, setRequests] = useState<PayrollRequiredActionRow[]>(
    () => payrollRequestsCache.get(businessId) ?? []
  );
  const [isLoading, setIsLoading] = useState(() => !payrollRequestsCache.has(businessId));
  const [error, setError] = useState<string | null>(null);

  const fetchRequests = useCallback(async () => {
    if (!businessId || !supabase) return;

    const { data, error: fetchError } = await supabase
      .from('payroll_request')
      .select(
        `
        id,
        request_type,
        status,
        requested_at,
        created_at,
        resource:resources(first_name, last_name)
      `
      )
      .eq('business_id', businessId)
      .eq('status', 'pending')
      .in('request_type', ['change', 'add', 'add_flat_rate'])
      .order('requested_at', { ascending: true })
      .limit(100);

    if (fetchError) throw fetchError;

    const mapped = (data ?? []).map((row) => {
      const resource = row.resource as { first_name?: string; last_name?: string } | null;
      const resourceName = resource
        ? `${resource.first_name || ''} ${resource.last_name || ''}`.trim()
        : '';
      return {
        id: row.id,
        request_type: row.request_type,
        status: row.status,
        requested_at: row.requested_at,
        created_at: row.created_at,
        resource_name: resourceName || undefined,
      };
    });
    setRequests(mapped);
    payrollRequestsCache.set(businessId, mapped);
  }, [businessId, supabase]);

  const load = useCallback(async () => {
    if (!enabled || !businessId || !supabase) {
      setRequests([]);
      setIsLoading(false);
      return;
    }

    const hasCache = payrollRequestsCache.has(businessId);
    if (hasCache) {
      setRequests(payrollRequestsCache.get(businessId) ?? []);
      setIsLoading(false);
    } else {
      setIsLoading(true);
    }
    setError(null);
    try {
      await fetchRequests();
    } catch (err) {
      console.error('Error loading payroll required actions:', err);
      setError('Failed to load payroll requests.');
    } finally {
      setIsLoading(false);
    }
  }, [enabled, businessId, supabase, fetchRequests]);

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      setRequests([]);
      return;
    }
    void load();
  }, [load, enabled]);

  const refreshDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!enabled || !businessId || !supabase || isLoading) return;

    const channel = supabase
      .channel(`payroll-required-actions-${businessId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'payroll_request',
          filter: `business_id=eq.${businessId}`,
        },
        () => {
          if (refreshDebounceRef.current) clearTimeout(refreshDebounceRef.current);
          refreshDebounceRef.current = setTimeout(() => {
            void fetchRequests();
          }, 400);
        }
      )
      .subscribe();

    return () => {
      if (refreshDebounceRef.current) clearTimeout(refreshDebounceRef.current);
      void supabase.removeChannel(channel);
    };
  }, [enabled, businessId, supabase, isLoading, fetchRequests]);

  const payrollActions = useMemo(() => buildPayrollRequiredActions(requests), [requests]);

  return {
    requests,
    payrollActions,
    isLoading,
    error,
    refresh: load,
  };
}
